import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSupabaseTypedClient } from 'hooks/reactQuery';
import { useCallback, useRef } from 'react';

import { TanstackQueryName } from 'types/frontend';

import { CVData, CVSettings, CVTemplate } from '../types';

interface SaveCVParams {
  cvId: string;
  title?: string;
  template?: CVTemplate;
  data?: CVData;
  settings?: CVSettings;
}

export const useSaveCV = (cvId: string | undefined) => {
  const supabase = useSupabaseTypedClient();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (params: SaveCVParams) => {
      const updatePayload: Record<string, unknown> = {
        updated_at: new Date().toISOString(),
      };

      if (params.title !== undefined) updatePayload.title = params.title;
      if (params.template !== undefined) updatePayload.template = params.template;
      if (params.data !== undefined) updatePayload.data = params.data;
      if (params.settings !== undefined) updatePayload.settings = params.settings;

      const { error } = await supabase
        .from('user_cv')
        .update(updatePayload)
        .eq('id', params.cvId);

      if (error) throw error;
    },
    onSuccess: () => {
      if (cvId) {
        queryClient.invalidateQueries({ queryKey: [TanstackQueryName.GetCV, cvId] });
      }
    },
  });

  // Stabilize mutate ref to avoid recreating saveImmediately on every render
  const mutateRef = useRef(mutation.mutate);
  mutateRef.current = mutation.mutate;

  const saveImmediately = useCallback(
    (params: Omit<SaveCVParams, 'cvId'>) => {
      if (!cvId) return;
      mutateRef.current({ cvId, ...params });
    },
    [cvId]
  );

  return {
    saveImmediately,
    isSaving: mutation.isPending,
  };
};
